import { ReactNode, memo, useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { ConversationPartApiResponse } from "@f2e/ui/chat-v2"
import {
  PartNativeConversationPanel,
  getConversationPartText,
  type AssistantActionBarMode,
  type ChatHistory,
  type ChatMessage,
  type ConversationPartRenderContext,
  type FeedbackBlockedReason,
  type FileBadgeData,
  type MessageBlockExtension,
  type ImageEditTarget,
  type HistoryAnalysisCancelHandler,
  type PaymentSummaryCardLabels,
  type PaymentSummaryData,
} from "@f2e/ui/components/Chat"

import ConversationPartFooter, { ActionType } from "./ConversationPartFooter"

/**
 * Copilot-aware conversation renderer.
 *
 * Pages pass copilotFieldPrefix when conversation parts should be exposed to
 * Copilot. When changing part filtering, grouping, metadata mapping, or the
 * user/assistant text rendering flow, preserve page-specific conversation
 * summaries. History depends on these summaries to expose user prompts,
 * assistant answers, and per-part agent name/version for lookup_agent when
 * that metadata exists.
 */

export type { ConversationPartRenderContext }

export interface WebConversationPartPanelProps {
  className?: string
  history?: ChatHistory
  parts: ConversationPartApiResponse[]
  isAsking?: boolean
  showExternalUserId?: boolean
  externalUserIdFallback?: string
  assistantActionBarMode?: AssistantActionBarMode
  resolveUrl?: (url: string) => string
  onDownload?: (file: FileBadgeData) => Promise<boolean>
  onPreviewLoad?: (file: FileBadgeData) => Promise<string>
  onEditImage?: (target: ImageEditTarget) => void
  onHelpfulFeedback?: (part: ConversationPartApiResponse) => Promise<boolean>
  onImproveFeedback?: (
    part: ConversationPartApiResponse,
    content?: string
  ) => Promise<boolean>
  onFeedbackBlocked?: (reason: FeedbackBlockedReason) => void
  submittingFormRequestId?: string | null
  onSubmitFormInteraction?: (
    part: ConversationPartApiResponse,
    values: Record<string, unknown>
  ) => void | Promise<void>
  onRejectFormInteraction?: (
    part: ConversationPartApiResponse
  ) => void | Promise<void>
  onCancelHistoryAnalysis?: HistoryAnalysisCancelHandler
  showHistoryAnalysisResumeGuidance?: boolean
  getHiddenActions?: (
    groupIndex: number,
    questionPart: ConversationPartApiResponse,
    answerPart: ConversationPartApiResponse
  ) => ActionType[]
  getRewriteTooltip?: (
    questionPart: ConversationPartApiResponse,
    answerPart: ConversationPartApiResponse
  ) => string | undefined
  onRegenerateGroup?: (groupId: string) => void
  onRewritePart?: (questionPart: ConversationPartApiResponse) => void
  onEditPart?: (
    questionPart: ConversationPartApiResponse,
    content: string
  ) => void
  onDeletePart?: (questionPart: ConversationPartApiResponse) => void
  onCreateEvalPart?: (
    questionPart: ConversationPartApiResponse,
    answerPart: ConversationPartApiResponse,
    allParts: ConversationPartApiResponse[]
  ) => void
  renderAssistantMetadata?: (args: {
    context: ConversationPartRenderContext
    questionPart: ConversationPartApiResponse
    answerPart: ConversationPartApiResponse
  }) => ReactNode
  copilotFieldPrefix?: string
  renderGroupFooter?: (
    context: ConversationPartRenderContext,
    groupIndex: number
  ) => ReactNode
  renderGroupSeparator?: (
    previousContext: ConversationPartRenderContext,
    nextContext: ConversationPartRenderContext
  ) => ReactNode
  renderBetweenParts?: (
    context: ConversationPartRenderContext,
    part: ConversationPartApiResponse,
    partIndex: number
  ) => ReactNode
  renderPartLabel?: (
    context: ConversationPartRenderContext,
    part: ConversationPartApiResponse,
    partIndex: number
  ) => ReactNode
  renderPart?: (
    context: ConversationPartRenderContext,
    part: ConversationPartApiResponse,
    defaultNode: ReactNode,
    partIndex: number
  ) => ReactNode
  renderPaymentSummary?: (
    context: ConversationPartRenderContext,
    message: ChatMessage,
    paymentSummary: PaymentSummaryData,
    defaultNode: ReactNode,
    partIndex: number
  ) => ReactNode
  filterAttachment?: (
    file: FileBadgeData,
    part: ConversationPartApiResponse
  ) => boolean
  messageBlockExtensions?: MessageBlockExtension[]
}

const getPartAgentProfileName = (metadata: Record<string, unknown>) => {
  const agentProfile = metadata.agent_profile ?? metadata.agentProfile
  if (!agentProfile || typeof agentProfile !== "object") return undefined
  const name = (agentProfile as { name?: unknown }).name
  return typeof name === "string" ? name : undefined
}

const getPartAgentVersion = (metadata: Record<string, unknown>) => {
  const agentVersion = metadata.agent_version ?? metadata.agentVersion
  return typeof agentVersion === "number" ? agentVersion : undefined
}

export const getConversationPartCopilotSnapshotValue = (
  part: ConversationPartApiResponse
) => {
  const agentName = getPartAgentProfileName(part.metadata)
  const agentVersion = getPartAgentVersion(part.metadata)
  const parts = [
    part.part_kind ? `kind=${part.part_kind}` : "",
    agentName ? `agentName=${agentName}` : "",
    typeof agentVersion === "number" ? `agentVersion=v${agentVersion}` : "",
    `content=${part.part_kind}: ${getConversationPartText(part)}`,
  ].filter(Boolean)

  return parts.join("; ")
}

const isPlatformReplyPart = (part: ConversationPartApiResponse) =>
  part.metadata.source === "platform_reply"

const platformReplyMessageContainerClassName = [
  "!mx-0 rounded-none bg-transparent !px-0 sm:!mx-0 sm:!px-0",
  "[&_.content]:my-0 [&_.content]:prose-p:my-0",
].join(" ")

const WebConversationPartPanelComponent = ({
  className,
  history,
  parts,
  isAsking = false,
  showExternalUserId = false,
  externalUserIdFallback,
  assistantActionBarMode = "custom",
  resolveUrl,
  onDownload,
  onPreviewLoad,
  onEditImage,
  onHelpfulFeedback,
  onImproveFeedback,
  onFeedbackBlocked,
  submittingFormRequestId,
  onSubmitFormInteraction,
  onRejectFormInteraction,
  onCancelHistoryAnalysis,
  showHistoryAnalysisResumeGuidance,
  getHiddenActions,
  getRewriteTooltip,
  onRegenerateGroup,
  onRewritePart,
  onEditPart,
  onDeletePart,
  onCreateEvalPart,
  renderAssistantMetadata,
  copilotFieldPrefix,
  renderGroupFooter,
  renderGroupSeparator,
  renderBetweenParts,
  renderPartLabel,
  renderPart,
  renderPaymentSummary,
  filterAttachment,
  messageBlockExtensions,
}: WebConversationPartPanelProps) => {
  const { t, i18n } = useTranslation()
  const timestampLocale = i18n.resolvedLanguage || i18n.language
  const paymentSummaryLabels = useMemo<PaymentSummaryCardLabels>(
    () => ({
      title: t("common.paymentInteraction.title"),
      defaultItemDesc: t("common.paymentInteraction.defaultItemDesc"),
      amount: t("common.paymentInteraction.amount"),
      orderNo: t("common.paymentInteraction.orderNo"),
      paidAt: t("common.paymentInteraction.paidAt"),
      tradeNo: t("common.paymentInteraction.tradeNo"),
      paymentMethod: t("common.paymentInteraction.paymentMethod"),
      getStatusLabel: (status: string) =>
        t(`common.paymentInteraction.status.${status}`, {
          defaultValue: status,
        }),
    }),
    [t]
  )

  return (
    <div data-testid="web-conversation-part-panel">
      <PartNativeConversationPanel
        className={className}
        history={history}
        parts={parts}
        isAsking={isAsking}
        showExternalUserId={showExternalUserId}
        assistantActionBarMode={assistantActionBarMode}
        timestampLocale={timestampLocale}
        externalUserIdFallback={externalUserIdFallback}
        resolveUrl={resolveUrl}
        onDownload={onDownload}
        onPreviewLoad={onPreviewLoad}
        onEditImage={onEditImage}
        onHelpfulFeedback={onHelpfulFeedback}
        onImproveFeedback={onImproveFeedback}
        onFeedbackBlocked={onFeedbackBlocked}
        submittingFormRequestId={submittingFormRequestId}
        onSubmitFormInteraction={onSubmitFormInteraction}
        onRejectFormInteraction={onRejectFormInteraction}
        onCancelHistoryAnalysis={onCancelHistoryAnalysis}
        showHistoryAnalysisResumeGuidance={showHistoryAnalysisResumeGuidance}
        onRegenerate={onRegenerateGroup}
        paymentSummaryLabels={paymentSummaryLabels}
        renderAssistantActionBar={({
          context,
          message,
          questionPart,
          questionContent,
          answerPart,
          allParts,
        }) => (
          <>
            {renderAssistantMetadata?.({
              context,
              questionPart,
              answerPart,
            })}
            <ConversationPartFooter
              history={history}
              message={message}
              questionPart={questionPart}
              questionContent={questionContent}
              answerPart={answerPart}
              allParts={allParts}
              hiddenActions={getHiddenActions?.(
                context.groupIndex,
                questionPart,
                answerPart
              )}
              rewriteTooltip={getRewriteTooltip?.(questionPart, answerPart)}
              onRewritePart={onRewritePart}
              onEditPart={onEditPart}
              onDeletePart={onDeletePart}
              onCreateEvalPart={onCreateEvalPart}
              onHelpfulFeedback={() =>
                onHelpfulFeedback?.(answerPart) ?? Promise.resolve(false)
              }
              onImproveFeedback={(content?: string) =>
                onImproveFeedback?.(answerPart, content) ??
                Promise.resolve(false)
              }
              onFeedbackBlocked={onFeedbackBlocked}
            />
          </>
        )}
        renderGroupFooter={renderGroupFooter}
        renderGroupSeparator={renderGroupSeparator}
        renderPartLabel={renderPartLabel}
        renderPart={renderPart}
        renderBetweenParts={(context, part, partIndex) => (
          <>
            {copilotFieldPrefix &&
              part.id != null &&
              (part.part_kind === "user-prompt" ||
                part.part_kind === "text") && (
                <div
                  data-copilot-field={`${copilotFieldPrefix}.${part.id}.summary`}
                  data-copilot-value={getConversationPartCopilotSnapshotValue(
                    part
                  )}
                  className="hidden"
                />
              )}
            {renderBetweenParts?.(context, part, partIndex)}
          </>
        )}
        renderPaymentSummary={renderPaymentSummary}
        filterAttachment={filterAttachment}
        getMessageContainerClassName={(_context, part) =>
          isPlatformReplyPart(part)
            ? platformReplyMessageContainerClassName
            : undefined
        }
        getPartWrapperClassName={(_context, part) =>
          isPlatformReplyPart(part) ? "py-4 [&>div:last-child]:mt-2" : undefined
        }
        messageBlockExtensions={messageBlockExtensions}
      />
    </div>
  )
}

export const WebConversationPartPanel = memo(WebConversationPartPanelComponent)

export default WebConversationPartPanel
